import ApiHandler from './ApiHandler';
import CryptoJS from 'crypto-js';

interface AppTotals {
    maxLevelFinished: number;
    score: number;
}

interface AppLevel {
    id: number;
    score: number;
}

interface AppSettings {
    soundVolume: number;
    gameSpeed: number;
}

interface AppUser {
    id: number;
    name: string;
    fingerprint: string;
}

const eK = 'hjgshjkiwggi8429';

export default class AppData {
    totals: AppTotals;
    levels: AppLevel[];
    settings: AppSettings;
    user: AppUser;
    globalScores: any[] = [];
    levelScores: any[] = [];

    
    constructor(
        totals: AppTotals,
        levels: AppLevel[],
        settings: { soundVolume: number; gameSpeed: number },
        user: { id:  integer, name: string, fingerprint: string },
    ) {
        this.checkTotals(totals);
        this.checkLevels(levels);
        this.checkSettings(settings);
        this.checkUser(user);

        this.totals = totals;
        this.levels = levels;
        this.settings = settings;
        this.user = user;
    }

    static createEmpty(): AppData {

        return new AppData(
            { maxLevelFinished: 0 , score: 0},
            [],
            { soundVolume: 0.3, gameSpeed: 0.3 },
            { id: 0, name: '', fingerprint: '' },
        );
    }

    private checkTotals(totals: AppTotals): void {
        if (
            typeof totals !== 'object' ||
            typeof totals.maxLevelFinished !== 'number' ||
            typeof totals.score !== 'number'
        ) {
            throw new Error('Invalid totals object');
        }
    }

    private checkLevels(levels: AppLevel[]): void {
        if (!Array.isArray(levels)) {
            throw new Error('Levels must be an array');
        }
        for (const level of levels) {
            if (
                typeof level !== 'object' ||
                typeof level.id !== 'number' ||
                typeof level.score !== 'number'
            ) {
                throw new Error('Invalid level object');
            }
        }
    }

    private checkSettings(settings: AppSettings): void {
        if (
            typeof settings !== 'object' ||
            typeof settings.soundVolume !== 'number' ||
            typeof settings.gameSpeed !== 'number'
        ) {
            throw new Error('Invalid settings object');
        }
    }

    private checkUser(user: AppUser): void {
        if (
            typeof user !== 'object' ||
            typeof user.name !== 'string' ||
            typeof user.fingerprint !== 'string'
        ) {
            throw new Error('Invalid settings object');
        }
    }

    public saveLocal(key: string): void {
        let dataToStore: string;
    
        switch (key) {
        case 'totals':
            dataToStore = JSON.stringify(this.totals);
            break;
        case 'levels':
            dataToStore = JSON.stringify(this.levels);
            break;
        case 'settings':
            dataToStore = JSON.stringify(this.settings);
            break;
        case 'user':
            dataToStore = JSON.stringify(this.user);
            break;
        case 'globalScores':
            dataToStore = JSON.stringify(this.globalScores);
            break;
        case 'levelScores':
            dataToStore = JSON.stringify(this.levelScores);
            break;
        default:
            console.error(`Key '${key}' is not valid to local save.`);
            return;
        }
    
        // Encripta los datos antes de guardarlos
        const encryptedData = CryptoJS.AES.encrypt(dataToStore, eK).toString();
        localStorage.setItem(`AppData.${key}`, encryptedData);
    }

    async saveRemote(key: string): Promise<void> {

        switch (key) {
        case 'levels': {
            const levelUpdates = this.levels.map(level => {
                return {
                    score: level.score,
                    level: level.id,
                    user_id: this.user.id
                };
            });
                
            return ApiHandler.saveLevels(levelUpdates)
                .then(() => {
                    
                });
        }
            break;
        case 'user': {
            return ApiHandler.saveUser({
                fingerprint: this.user.fingerprint,
                name: this.user.name
            }).then(user => {
                this.user.id = user.id;
            });
        }
            break;
        default:
            console.error(`Key '${key}' is not valid to remote save.`);
        }
    }

    public save(): void {
        this.saveTotals();
        this.saveSettings();
        this.saveUser();
        this.saveLevels();
    }

    public saveTotals(): void {
        this.saveLocal('totals');
    }

    public saveGlobalScores(): void {
        this.saveLocal('globalScores');
    }

    public saveLevelScores(): void {
        this.saveLocal('levelScores');
    }

    public saveSettings(): void {
        this.saveLocal('settings');
    }

    public saveUser(): void {
        this.saveRemote('user')
            .finally(() => {
                this.saveLocal('user');
            });
    }

    public saveLevels(): void {
        this.saveRemote('levels')
            .finally(() => {
                this.saveLocal('levels');
            });
    }

    static getLocal(key: string): any {
        const encryptedData = localStorage.getItem(`AppData.${key}`);
        if (!encryptedData) {
            throw new Error(`Error getting local: ${key}`);
        }

        const decryptedData = CryptoJS.AES.decrypt(encryptedData, eK).toString(CryptoJS.enc.Utf8);
        return JSON.parse(decryptedData);
    }

    static fromLocalStorage(): AppData | null {
        try {
            return new AppData(
                AppData.getLocal('totals'),
                AppData.getLocal('levels'),
                AppData.getLocal('settings'),
                AppData.getLocal('user'),
            );
        } catch (_error) {
            return null;
        }
    }

    static async fromExternalStorage(fingerprint: string ): Promise<AppData | null> {

        try {
            const user = await ApiHandler.getUserByFingerprint(fingerprint);
            const levels = await ApiHandler.getLevelsByUser(user.id);

            const totalScore = levels.reduce((sum: any, level: any) => {
                return sum + level.score;
            }, 0);
            
            return new AppData(
                { maxLevelFinished: levels[levels.length - 1].id, score: totalScore },
                levels,
                { soundVolume: 0.3, gameSpeed: 0.3 },
                { id: user.id, name: user.name, fingerprint: user.fingerprint }
            );
        } catch (_error) {
            return null;
        }
    }

    async refreshScores(): Promise<void> {
        try {
            this.globalScores = await ApiHandler.getTopGlobalScores();
            this.saveGlobalScores();
        } catch (_error) {
            this.globalScores = AppData.getLocal('globalScores');
        }

        try {
            this.levelScores = await ApiHandler.getTopLevelScores();
            this.saveLevelScores();
        } catch (_error) {
            this.levelScores = AppData.getLocal('levelScores');
        }
    }

    public completeLevel(levelId: number, score: number): void {

        let newScore:integer = this.totals.score;
        // New Level Completed
        if (levelId > this.totals.maxLevelFinished) {
            this.totals.maxLevelFinished = levelId;
            newScore = this.totals.score + score;
            this.levels.push({ id: levelId, score: score });
        }

        // Past level Improved
        this.levels = this.levels.map((item) => {
        
            if (item.id == levelId && item.score < score) {
                newScore = this.totals.score - item.score + score;
                item.score = score;
            }
            return item;
        });

        // Is Score Changed
        if(newScore != this.totals.score) {
            this.totals.score = newScore;
            this._manageGlobalScores({user_id: this.user.id, name: this.user.name, total_score: newScore});
            this._manageLevelScores({user_id: this.user.id, name: this.user.name, score: score}, levelId);
        }

        this.saveLocal('totals');
        this.saveLocal('levels');
        this.saveLocal('globalScores');
        this.saveLocal('levelScores');
    }

    private _manageGlobalScores(newScore: any): void {
        // Required name
        if(newScore.name === '') {
            return;
        }
        // is in globalScores
        const index = this.globalScores.findIndex(score => score.user_id === newScore.user_id);
        if (index !== -1) {
            this.globalScores[index].total_score = newScore.total_score;
        // not is in globalStores
        } else {
            this.globalScores.push(newScore);
        }
        // sort
        this.globalScores.sort((a, b) => b.total_score - a.total_score);
        this.globalScores = this.globalScores.slice(0, 10);
    }

    private _manageLevelScores(newScore: any, levelId: integer): void {
        if(newScore.name === '') {
            return;
        }

        if (this.levelScores[levelId] === undefined) {
            this.levelScores[levelId] = [];
            this.levelScores[levelId].push(newScore);
            return;
        }

        const index = this.levelScores[levelId].findIndex((score: any) => score.user_id === newScore.user_id);
        if (index !== -1) {
            this.levelScores[levelId][index].score = newScore.score;
        } else {
            this.levelScores[levelId].push(newScore);
        }

        this.levelScores[levelId].sort((a: any, b: any) => b.score - a.score);
        this.levelScores[levelId] = this.levelScores[levelId].slice(0, 3);
    }
        

    public setSoundVolume(volume: number): void {
        this.settings.soundVolume = volume;
        this.saveSettings();
    }

    public setGameSpped(speed: number): void {
        this.settings.gameSpeed = speed;
        this.saveSettings();
    }

    public setUserName(name: string): void {
        if(this.user.name === name) {
            return;
        }

        // change user.name
        this.user.name = name;
        this.saveUser();

        // change global scores
        const index = this.globalScores.findIndex(user => user.user_id === this.user.id);
        if (index !== -1) {
            this.globalScores[index].name = name;
        }
        this.saveGlobalScores();

        // change level scores
        for(const levelId in this.levelScores) {
            const index = this.levelScores[levelId].findIndex((user: any) => user.user_id === this.user.id);
            if (index !== -1) {
                this.levelScores[levelId][index].name = name;
            }
        }
        this.saveLevelScores();  
    }

    public gameSpeedFactor(): number {
        return (2.5*this.settings.gameSpeed + 0.5);
    }

    public reset(): void {
        localStorage.removeItem('AppData');
    }

    public getScoreLevel(levelId: integer): integer {
        const level = this.levels.find((level: any) => {
            return level.id == levelId;
        });
        if (!level) {
            return 0;
        }
        return level.score;
    }
}